import React from 'react';
import { useSelector } from 'react-redux';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';

import { constants as sessionConstants, selectors as sessionSelectors } from '../redux/session';
import { PrivateRoute } from '../components';
import {
  Account,
  ChangePassword,
  ConfirmPasswordReset,
  DownloadArchive,
  FallbackPage,
  Home,
  Login,
  PhotoSeriesDetails,
  ResetPassword,
  Vehicles,
  PublicAccess,
  AskAdditionalInformation,
  Translations,
  PhotoSeriesImport,
  UserAgentBlacklist,
  CustomDamageTypes,
  Workflows,
  Branding,
  CustomImageTypes,
  Statistics,
  WindshieldRepairabilityProfiles,
  VehiclesExported,
  LinksExported,
  Representative,
  PrivacyPolicy,
  AccessUrls,
  Settings
} from '../screens';
import { GlobalErrorEngine } from '../containers';

import ConfirmEmail from '../screens/ConfirmEmail/ConfirmEmail';

import '../assets/css/bootstrap.min.css';
import '../app.scss';

export const routes = {
  account: '/account',
  accessUrls: '/accessUrls/:id',
  askAdditionalInformation: '/askAdditionalInformation',
  changePassword: '/changePassword',
  confirmEmail: '/confirmEmail',
  confirmPasswordReset: '/confirmPasswordReset',
  downloadArchive: '/downloadArchive',
  fallback: '/error',
  home: '/',
  login: '/login',
  photoSeriesDetails: '/vehicles/:vehicleId/photoSeries/:photoSeriesId',
  publicAccess: '/access/:accessUrlId',
  resetPassword: '/resetPassword/:email',
  translations: '/translations',
  vehicles: '/vehicles',
  vehiclesExported: '/exported-vehicles',
  linksExported: '/exported-links',
  photoSeriesImport: '/photoSeries/import',
  userAgentBlacklist: '/userAgentBlacklist',
  customDamageTypes: '/customDamageTypes',
  customImageTypes: '/customImageTypes',
  workflows: '/workflows',
  branding: '/branding',
  statistics: '/statistics',
  windshieldRepairabilityProfiles: '/windshieldRepairabilityProfiles',
  representative: '/representative',
  privacyPolicy: '/privacy-policy',
  settings: '/settings',
};

const {
  EDIT_CUSTOMER_CONFIGURATION, EDIT_TRANSLATIONS,
  IMPORT_PHOTO_SERIES, EDIT_BLACKLISTED_USER_AGENT, VIEW_CUSTOM_DAMAGE_TYPES,
  EDIT_WORKFLOWS, EDIT_CUSTOM_IMAGE_TYPE, VIEW_STATISTICS, VIEW_WINDSHIELD_REPAIRABILITY_PROFILES,
  EXPORT_VEHICLES, EXPORT_ACCESS_URLS, VIEW_VEHICLES, CREATE_ACCESS_URLS
} = sessionConstants.PERMISSION_TYPES;

const Routes = () => {
  const isGlobalConfEditCustomer = useSelector(sessionSelectors.getIsGlobalConfEditCustomer);

  return (
    <GlobalErrorEngine>
      <Router>
        <Switch>
          <Route exact path={routes.publicAccess} component={PublicAccess} />
          <Route exact path={routes.login} component={Login} />
          <Route exact path={routes.changePassword} component={ChangePassword} />
          <Route exact path={routes.resetPassword} component={ResetPassword} />
          <Route exact path={routes.confirmPasswordReset} component={ConfirmPasswordReset} />
          <PrivateRoute exact path={routes.home} component={Home} requiredPermissions={[VIEW_VEHICLES]} logOut />
          <PrivateRoute exact path={routes.account} component={Account} requiredPermissions={[EDIT_CUSTOMER_CONFIGURATION]} />
          <PrivateRoute exact path={routes.downloadArchive} component={DownloadArchive} />
          <PrivateRoute exact path={routes.vehicles} component={Vehicles} requiredPermissions={[VIEW_VEHICLES]} />
          <PrivateRoute exact path={routes.vehiclesExported} component={VehiclesExported} requiredPermissions={[EXPORT_VEHICLES]} />
          <PrivateRoute exact path={routes.linksExported} component={LinksExported} requiredPermissions={[EXPORT_ACCESS_URLS]} />
          <PrivateRoute exact path={routes.accessUrls} component={AccessUrls} requiredPermissions={[CREATE_ACCESS_URLS]} />
          <PrivateRoute exact path={routes.translations} component={Translations} requiredPermissions={[EDIT_TRANSLATIONS]} />
          <PrivateRoute exact path={routes.photoSeriesDetails} component={PhotoSeriesDetails} />
          <PrivateRoute exact path={routes.askAdditionalInformation} component={AskAdditionalInformation} />
          {isGlobalConfEditCustomer && (
            <PrivateRoute exact path={routes.photoSeriesImport} component={PhotoSeriesImport} requiredPermissions={[IMPORT_PHOTO_SERIES]} />
          )}
          {isGlobalConfEditCustomer && (
            <PrivateRoute exact path={routes.userAgentBlacklist} component={UserAgentBlacklist} requiredPermissions={[EDIT_BLACKLISTED_USER_AGENT]} />
          )}
          <PrivateRoute exact path={routes.customDamageTypes} component={CustomDamageTypes} requiredPermissions={[VIEW_CUSTOM_DAMAGE_TYPES]} />
          <PrivateRoute exact path={routes.workflows} component={Workflows} requiredPermissions={[EDIT_WORKFLOWS]} />
          <PrivateRoute exact path={routes.branding} component={Branding} requiredPermissions={[EDIT_CUSTOMER_CONFIGURATION]} />
          <PrivateRoute exact path={routes.customImageTypes} component={CustomImageTypes} requiredPermissions={[EDIT_CUSTOM_IMAGE_TYPE]} />
          {process.env.REACT_APP_SHOW_STATISTICS === 'true' && isGlobalConfEditCustomer && (
            <PrivateRoute exact path={routes.statistics} component={Statistics} requiredPermissions={[VIEW_STATISTICS]} />
          )}
          <PrivateRoute
            exact
            path={routes.windshieldRepairabilityProfiles}
            component={WindshieldRepairabilityProfiles}
            requiredPermissions={[VIEW_WINDSHIELD_REPAIRABILITY_PROFILES]}
          />
          <PrivateRoute exact path={routes.representative} component={Representative} />
          <PrivateRoute exact path={routes.settings} component={Settings} />
          <Route exact path={routes.confirmEmail} component={ConfirmEmail} />
          <Route exact path={routes.privacyPolicy} component={PrivacyPolicy} />
          <Route exact path={routes.fallback} component={FallbackPage} />
          <Route component={FallbackPage} />
        </Switch>
      </Router>
    </GlobalErrorEngine>
  );
};

export default Routes;
